import React from "react";
import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from "lucide-react";
import { Badge, BadgeProps } from "@/components/badges/Badge";
import { cx } from "class-variance-authority";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type BlockageStatusBadgeMode = "badge" | "dot";

interface BlockageStatusBadgeProps {
	status: Dryp.BlockageStatus;
	className?: string;
	iconClassName?: string;
	mode?: BlockageStatusBadgeMode;
	showLabel?: boolean;
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const statusMap: Record<
	Dryp.BlockageStatusValues,
	{ variant: BadgeVariant; Icon: React.ComponentType<{ className?: string; fill?: string }> }
> = {
	NoData: { variant: "unknown", Icon: HelpCircle },
	Normal: { variant: "success", Icon: CheckCircle2 },
	Warning: { variant: "warning", Icon: AlertTriangle },
	Blocked: { variant: "blocked", Icon: XCircle },
	Full: { variant: "full", Icon: XCircle },
	Undetermined: { variant: "undetermined", Icon: XCircle },
};

export const BlockageStatusBadge: React.FC<BlockageStatusBadgeProps> = ({
	status,
	className = "",
	iconClassName = "",
	mode = "badge",
	showLabel = true,
}) => {
	const badgeConfig = statusMap[status.status];
	if (!badgeConfig) return null;

	const { variant, Icon } = badgeConfig;

	if (mode === "dot") {
		const content = (
			<div className={cx("flex items-center gap-2 text-xs", className)}>
				<Badge variant={variant} className="w-[18px] h-[18px] rounded-full p-0 border-none" />
				{showLabel && <span>{status.statusLabel}</span>}
			</div>
		);

		if (!status.statusDetail) return content;

		return (
			<Tooltip>
				<TooltipTrigger asChild>{content}</TooltipTrigger>
				<TooltipContent className="max-w-xs break-words">
					<p>{status.statusDetail}</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Badge variant={variant} className={cx("gap-1 px-1.5 py-0.5", className)}>
					<Icon className={`h-3 w-3 shrink-0 ${iconClassName}`} />
					{status.statusLabel}
				</Badge>
			</TooltipTrigger>

			{status.statusDetail && (
				<TooltipContent className="max-w-xs break-words">
					<p>{status.statusDetail}</p>
				</TooltipContent>
			)}
		</Tooltip>
	);
};
